import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Keyboard, Platform, Pressable, useWindowDimensions, View } from "react-native";
import { parse, isValid, startOfDay } from "date-fns";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Text } from "../Text";
import { Calendar } from "./Calendar";
import { Popover } from "./Popover";
import { usePopover } from "./usePopover";
import { defaultRangePresets, type DateRangePreset } from "./presets";
import {
  clampRange,
  formatDisplayDate,
  formatDisplayRange,
  isDateDisabled,
  isSameDay,
  maskDateRangeInput,
  orderRange,
  type DateRange,
  type WeekStartsOn,
} from "./date-utils";

export interface DateRangePickerProps {
  /** Currently selected range (controlled). */
  readonly value: DateRange;
  /** Called when the range changes (or is cleared → `{ start: null, end: null }`). */
  readonly onChange: (range: DateRange) => void;
  /** Floating field label. */
  readonly label?: string | undefined;
  /** Placeholder shown when no range is selected. */
  readonly placeholder?: string | undefined;
  /** Earliest selectable date (inclusive). */
  readonly minDate?: Date | undefined;
  /** Latest selectable date (inclusive). */
  readonly maxDate?: Date | undefined;
  /** Disables the field. */
  readonly disabled?: boolean | undefined;
  /** Error message; turns the border red and shows below the field. */
  readonly error?: string | undefined;
  /** Neutral helper text below the field. */
  readonly helperText?: string | undefined;
  /** Shows a clear button when a range is selected. */
  readonly clearable?: boolean | undefined;
  /** `date-fns` format for each displayed bound. Defaults to `d MMM yyyy`. */
  readonly format?: string | undefined;
  /** First day of the week (0 = Sunday … 6 = Saturday). Defaults to Monday. */
  readonly weekStartsOn?: WeekStartsOn | undefined;
  /** Reference "today" for presets and the today marker. Defaults to the current day. */
  readonly today?: Date | undefined;
  /** Quick-select shortcuts. Defaults to 1 day / 1 week / 1 fortnight / 1 month / 3 months. */
  readonly presets?: readonly DateRangePreset[] | undefined;
  /** Hides the preset shortcuts column. */
  readonly hidePresets?: boolean | undefined;
  /** Parse a single date from typed text. Defaults to `dd/MM/yyyy` parsing. */
  readonly parseInput?: ((text: string) => Date | null) | undefined;
  /** Format a Date for the editable input field. Defaults to `dd/MM/yyyy`. */
  readonly formatInput?: ((date: Date) => string) | undefined;
  /** Mask raw keyboard input (auto-insert separators). Return the masked string. */
  readonly maskInput?: ((raw: string, previous: string) => string) | undefined;
  /** Placeholder shown while the input is focused/editable. Defaults to `DD/MM/YYYY - DD/MM/YYYY`. */
  readonly inputPlaceholder?: string | undefined;
}

const INPUT_FORMAT = "dd/MM/yyyy";
const INPUT_PLACEHOLDER = "DD/MM/YYYY";
const RANGE_SEPARATOR = " - ";
const FULL_PLACEHOLDER = `${INPUT_PLACEHOLDER}${RANGE_SEPARATOR}${INPUT_PLACEHOLDER}`;

function defaultFormatInput(d: Date): string {
  return formatDisplayDate(d, INPUT_FORMAT);
}

function parseInputDate(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const parsed = parse(trimmed, INPUT_FORMAT, new Date());
  return isValid(parsed) ? startOfDay(parsed) : null;
}

function parseInputRange(text: string, parseSingleDate: (t: string) => Date | null): DateRange | null {
  const parts = text.split(/\s*[\u2013\u2014-]\s*/).map((s) => s.trim());
  if (parts.length === 1) {
    const single = parseSingleDate(parts[0] ?? "");
    if (single) return { start: single, end: single };
    return null;
  }
  if (parts.length === 2) {
    const start = parseSingleDate(parts[0] ?? "");
    const end = parseSingleDate(parts[1] ?? "");
    if (start && end) return orderRange(start, end);
    if (start) return { start, end: null };
    return null;
  }
  return null;
}

/**
 * Date range picker — an editable {@link Input} that accepts typed dates
 * (DD/MM/YYYY - DD/MM/YYYY) or opens a calendar popover via the icon button.
 * Two taps select a range; presets set it in one tap.
 */
const DateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = "Select range",
  minDate,
  maxDate,
  disabled = false,
  error,
  helperText,
  clearable = false,
  format = "dd/MM/yyyy",
  weekStartsOn = 1,
  today: todayProp,
  presets = defaultRangePresets,
  hidePresets = false,
  parseInput = parseInputDate,
  formatInput = defaultFormatInput,
  maskInput = maskDateRangeInput,
  inputPlaceholder = FULL_PLACEHOLDER,
}) => {
  const { theme } = useUnistyles();
  const { isOpen, toggle, close, open, containerRef } = usePopover();
  const today = useMemo(() => startOfDay(todayProp ?? new Date()), [todayProp]);

  const displayValue = formatDisplayRange(value, format);
  const editableValue = formatDisplayRange(value, INPUT_FORMAT, RANGE_SEPARATOR, formatInput);

  const [inputText, setInputText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const committedRef = useRef(false);
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;

  useEffect(() => {
    if (!isFocused) {
      setInputText(editableValue);
    }
  }, [editableValue, isFocused]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    committedRef.current = false;
    setInputText(editableValue);
    // On native the soft keyboard would overlap the calendar, so only
    // auto-open the popover on web. Native users open via the icon button.
    if (Platform.OS === "web") {
      open();
    } else {
      close();
    }
  }, [editableValue, open, close]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (committedRef.current) return;

    const trimmed = inputTextRef.current.trim();
    if (!trimmed) {
      onChange({ start: null, end: null });
      return;
    }

    const parsed = parseInputRange(trimmed, parseInput);
    if (parsed?.start && parsed.end) {
      const clamped = clampRange(parsed, minDate, maxDate);
      if (clamped?.start && clamped.end) {
        onChange(clamped);
        return;
      }
    }
    setInputText(editableValue);

    // Close popover after a tick so calendar press events can fire first.
    requestAnimationFrame(() => {
      if (!committedRef.current) close();
    });
  }, [editableValue, onChange, minDate, maxDate, close, parseInput]);

  const handleCommit = useCallback(
    (range: DateRange) => {
      committedRef.current = true;
      onChange(range);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setInputText("");
    onChange({ start: null, end: null });
  }, [onChange]);

  const handleChangeText = useCallback(
    (text: string) => {
      setInputText(maskInput(text, inputTextRef.current));
    },
    [maskInput],
  );

  const handleToggleCalendar = useCallback(() => {
    if (Platform.OS !== "web") {
      Keyboard.dismiss();
    }
    toggle();
  }, [toggle]);

  const anchor = (
    <Input
      value={isFocused ? inputText : displayValue}
      onChangeText={handleChangeText}
      placeholder={isFocused ? inputPlaceholder : placeholder}
      label={label}
      error={error}
      helperText={helperText}
      disabled={disabled}
      active={isOpen}
      clearable={clearable}
      onClear={handleClear}
      onFocus={handleFocus}
      onBlur={handleBlur}
      accessibilityLabel={label ?? placeholder}
      rightContent={
        <Pressable onPress={handleToggleCalendar} hitSlop={8} accessibilityLabel="Open calendar">
          <Icon name="calendarOutline" size={18} color={theme.colors.textMuted} />
        </Pressable>
      }
    />
  );

  return (
    <Popover isOpen={isOpen} onClose={close} containerRef={containerRef} anchor={anchor}>
      <RangeBody
        key={isOpen ? "open" : "closed"}
        value={value}
        today={today}
        weekStartsOn={weekStartsOn}
        minDate={minDate}
        maxDate={maxDate}
        presets={hidePresets ? [] : presets}
        onCommit={handleCommit}
        onComplete={close}
      />
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// Range body — owns the in-progress draft selection and hover preview.
// ---------------------------------------------------------------------------

interface RangeBodyProps {
  readonly value: DateRange;
  readonly today: Date;
  readonly weekStartsOn: WeekStartsOn;
  readonly minDate?: Date | undefined;
  readonly maxDate?: Date | undefined;
  readonly presets: readonly DateRangePreset[];
  readonly onCommit: (range: DateRange) => void;
  readonly onComplete: () => void;
}

const RangeBody: FC<RangeBodyProps> = ({
  value,
  today,
  weekStartsOn,
  minDate,
  maxDate,
  presets,
  onCommit,
  onComplete,
}) => {
  const { theme } = useUnistyles();
  const { width: windowWidth } = useWindowDimensions();
  const [draft, setDraft] = useState<DateRange>(value);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // On narrow viewports, stack presets above the calendar and shrink cells to
  // fit within the available dropdown width. The dropdown has 32px total
  // horizontal padding (16px each side) plus 16px page margin on each side.
  const dropdownPadding = 32;
  const pageMargin = 32;
  const availableWidth = windowWidth - dropdownPadding - pageMargin;
  const isCompact = availableWidth < 400;
  const cellSize = isCompact ? Math.max(28, Math.floor(availableWidth / 7)) : undefined;

  // Keep the in-progress draft in sync when the controlled value changes from
  // outside while the popover is open (e.g. the input's clear button resets it
  // to empty). Keyed on the actual date values so an inline `value` object does
  // not clobber a mid-selection draft on every render.
  const valueStartTime = value.start?.getTime() ?? null;
  const valueEndTime = value.end?.getTime() ?? null;
  useEffect(() => {
    setDraft({ start: value.start, end: value.end });
    setHoveredDate(null);
  }, [valueStartTime, valueEndTime]);

  const selectingEnd = draft.start != null && draft.end == null;

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (isDateDisabled(date, minDate, maxDate)) return;
      const day = startOfDay(date);

      const pendingStart = draft.start;
      if (!selectingEnd || pendingStart == null) {
        // Begin a new selection.
        setDraft({ start: day, end: null });
        setHoveredDate(null);
        return;
      }

      // Complete the range (ordered) and commit.
      const ordered = orderRange(pendingStart, day);
      setDraft(ordered);
      setHoveredDate(null);
      onCommit(ordered);
      onComplete();
    },
    [selectingEnd, draft.start, minDate, maxDate, onCommit, onComplete],
  );

  const handlePreset = useCallback(
    (preset: DateRangePreset) => {
      // Clamp to the picker bounds so a preset can never commit dates the
      // calendar itself would disable. Fully out-of-bounds presets are no-ops.
      const range = clampRange(preset.getRange(today), minDate, maxDate);
      if (!range) return;
      setDraft(range);
      onCommit(range);
      onComplete();
    },
    [today, minDate, maxDate, onCommit, onComplete],
  );

  // Resolve each preset's bounded range once: `null` means it falls entirely
  // outside [minDate, maxDate] and should be disabled.
  const resolvedPresets = useMemo(
    () => presets.map((preset) => ({ preset, range: clampRange(preset.getRange(today), minDate, maxDate) })),
    [presets, today, minDate, maxDate],
  );

  const activePresetId = useMemo(() => {
    const start = draft.start;
    const end = draft.end;
    if (!start || !end) return undefined;
    const match = resolvedPresets.find(
      ({ range }) =>
        range?.start != null && range.end != null && isSameDay(range.start, start) && isSameDay(range.end, end),
    );
    return match?.preset.id;
  }, [resolvedPresets, draft.start, draft.end]);

  // In compact mode, reorder presets so flex-wrap (left→right) reads as
  // column-first (top→bottom per column, then next column).
  const displayPresets = useMemo(() => {
    if (!isCompact) return resolvedPresets;
    const half = Math.ceil(resolvedPresets.length / 2);
    const reordered: typeof resolvedPresets = [];
    for (let i = 0; i < half; i++) {
      const left = resolvedPresets[i];
      if (left) reordered.push(left);
      const right = resolvedPresets[i + half];
      if (right) reordered.push(right);
    }
    return reordered;
  }, [resolvedPresets, isCompact]);

  return (
    <View style={[styles.body, isCompact && styles.bodyCompact]}>
      {displayPresets.length > 0 && (
        <View style={[styles.presets, isCompact && styles.presetsCompact]}>
          {displayPresets.map(({ preset, range }) => {
            const active = preset.id === activePresetId;
            const disabled = range == null;
            return (
              <Pressable
                key={preset.id}
                onPress={() => handlePreset(preset)}
                disabled={disabled}
                style={[
                  styles.preset,
                  isCompact && styles.presetCompact,
                  active && { backgroundColor: theme.colors.blue[500] },
                  disabled && styles.presetDisabled,
                ]}
                accessibilityRole="button"
                accessibilityState={{ selected: active, disabled }}
              >
                <Text
                  textStyle={active ? "sm/semibold" : "sm/regular"}
                  style={{ color: active ? theme.colors.white.default : theme.colors.text }}
                >
                  {preset.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}

      <Calendar
        weekStartsOn={weekStartsOn}
        today={today}
        defaultMonth={draft.start ?? value.start ?? today}
        selectedStart={draft.start}
        selectedEnd={draft.end}
        hoveredDate={hoveredDate}
        rangeMode
        minDate={minDate}
        maxDate={maxDate}
        onSelectDate={handleSelectDate}
        onHoverDate={selectingEnd ? setHoveredDate : undefined}
        cellSize={cellSize}
      />
    </View>
  );
};

RangeBody.displayName = "DateRangeBody";
DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker };

const styles = StyleSheet.create((theme) => ({
  body: {
    flexDirection: "row",
    gap: theme.spacing["3xl"],
  },
  bodyCompact: {
    flexDirection: "column",
    gap: theme.spacing.xl,
  },
  presets: {
    gap: theme.spacing.xs,
    minWidth: 120,
  },
  presetsCompact: {
    flexDirection: "row",
    flexWrap: "wrap",
    minWidth: 0,
    justifyContent: "space-between",
    rowGap: theme.spacing.xs,
  },
  preset: {
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.lg,
  },
  presetCompact: {
    width: "48%",
  },
  presetDisabled: {
    opacity: 0.4,
  },
}));
